import React, { useEffect, useState } from 'react';

import axios, { AxiosResponse } from 'axios';

import { Box } from '@clutter/clean';

import { NavLinkButton, Table, Panel } from '@shared/components/bootstrap';
import { Pluralize } from '@shared/components/helpers';
import { UserRole } from '@shared/types/user_role';

import { Roles } from '@admin/components/helpers/roles';
import { Spinner } from '@admin/components/spinner';
import { WarehouseSelectFormGroup } from '@admin/components/warehouses/select_form_group';
import { useAxiosRequest } from '@admin/hooks';
import { useTitle } from '@admin/hooks/use_title';

type Warehouse = {
  id: number;
  name: string;
};

type TransferPool = {
  id: string;
  origin: Warehouse;
  destination: Warehouse;
  pending_count: number;
  ready_count: number;
  transfers_count: number;
};

const useTransferPoolsRequest = ({ originID, destinationID }: { originID?: string; destinationID?: string }) => {
  const [request, setRequest] = useState<Promise<AxiosResponse<TransferPool[]>> | undefined>();

  useEffect(() => {
    setRequest(
      axios.get<TransferPool[]>('/transferpools.json', {
        params: { origin_id: originID, destination_id: destinationID },
      }),
    );
  }, [originID, destinationID]);

  return useAxiosRequest(request);
};

export const TransferPoolList: React.FC = () => {
  useTitle('Transfer Pool');
  const [originID, setOriginID] = useState<string | undefined>(undefined);
  const [destinationID, setDestinationID] = useState<string | undefined>(undefined);
  const { response, loading } = useTransferPoolsRequest({ originID, destinationID });
  const results = response?.data;

  return (
    <Panel>
      <Panel.Body>
        <Box.Flex gap="4px" alignItems="center" justifyContent="space-between">
          <Box.Flex gap="4px">
            <WarehouseSelectFormGroup placeholder="- Origin -" value={originID} onChange={setOriginID} />
            <WarehouseSelectFormGroup placeholder="- Destination -" value={destinationID} onChange={setDestinationID} />
          </Box.Flex>
          <NavLinkButton kind="default" to="/transfers">
            View Transfer Orders
          </NavLinkButton>
        </Box.Flex>
        <Table responsive>
          <thead>
            <tr>
              <th className="col-sm-3 col-nowrap text-left">Origin Warehouse</th>
              <th className="col-sm-3 col-nowrap text-left">Destination Warehouse</th>
              <th className="col-sm-2 col-nowrap text-left">Pending Pallets</th>
              <th className="col-sm-2 col-nowrap text-left">Ready Pallets</th>
              <th className="col-sm-2 col-nowrap text-left">Active Transfers</th>
              <th className="col-zero col-nowrap"></th>
            </tr>
          </thead>
          <tbody>
            {results?.map((result) => (
              <tr key={result.id}>
                <td className="col-sm-3 col-nowrap text-left">{result.origin.name}</td>
                <td className="col-sm-3 col-nowrap text-left">{result.destination.name}</td>
                <td className="col-sm-2 col-nowrap text-left">
                  <Pluralize count={result.pending_count} singular="pallet" plural="pallets" />
                </td>
                <td className="col-sm-2 col-nowrap text-left">
                  <Pluralize count={result.ready_count} singular="pallet" plural="pallets" />
                </td>
                <td className="col-sm-2 col-nowrap text-left">
                  <Pluralize count={result.transfers_count} singular="transfer" plural="transfers" />
                </td>
                <td className="col-zero col-nowrap">
                  <NavLinkButton kind="primary" to={`/transferpools/${result.id}`}>
                    View
                  </NavLinkButton>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        {loading && <Spinner />}
      </Panel.Body>
      <Roles show={[UserRole.Admin, UserRole.Manager]}>
        <Panel.Footer align="right">
          <NavLinkButton kind="mint" to="/transferables">
            Request Pallet Transfer
          </NavLinkButton>
        </Panel.Footer>
      </Roles>
    </Panel>
  );
};
